import type { RealtimeClient } from '../subscriptions';

/**
 * Keeps the shore client's realtime service to a single connection attempt at a
 * time.
 *
 * The client holds one pusher instance in a closure and replaces it on every
 * connect that finds no live connection — including one that is still
 * mid-handshake. Two callers connecting at once therefore leave the first
 * holding a promise for an instance the client has already forgotten: it
 * resolves `connected`, but the `subscribe()` that follows reads the *current*
 * instance, finds it unconnected and throws `No connections are available. try
 * to connect!`. The replaced socket stays open, unsubscribed, for the session.
 *
 * The shell has had more than one caller since the appointment-created push
 * joined the SMS allowance listener, and hands this same client to every micro
 * app, so it is the layer that has to keep the connection single.
 *
 * Callers arriving while an attempt is in flight share its result instead of
 * starting a second one. The memo is dropped once that attempt settles, so a
 * later call still reaches the client: on a live connection that refreshes the
 * auth header sent with every subsequent subscribe, and on a dropped one it
 * rebuilds the connection.
 */
export function shareRealtimeConnection(
  realtime: RealtimeClient
): RealtimeClient {
  let pending: Promise<string> | null = null;

  return {
    ...realtime,

    connect() {
      if (pending) return pending;

      const attempt = realtime.connect();
      pending = attempt;

      // Deliberately not `finally`: the rejection stays the caller's to handle,
      // and this must not become a second, silent consumer of it.
      const release = () => {
        if (pending === attempt) pending = null;
      };
      void attempt.then(release, release);

      return attempt;
    },
  };
}
